import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  pointerWithin,
  useDraggable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { Button, Modal, useLastPresent } from '@nsi-advisory-services/components';
import ExpenseKanbanCard from './ExpenseKanbanCard';
import ExpenseKanbanColumn from './ExpenseKanbanColumn';
import {
  EXPENSE_STAGES,
  STAGE_INVOICED,
  STAGE_PAID,
  evaluateExpenseTransition,
  getExpenseStage,
  getInvoicedAmount,
  getStageTitle,
} from '../../utils/expenseWorkflow';
import { buildExpenseGroupBlocks } from '../../utils/expenseGroups';
import './ExpensePayrollKanban.css';

// A card only becomes draggable after the pointer travels 6px, so a plain click
// still opens the expense instead of starting a drag.
const DRAG_ACTIVATION_DISTANCE = 6;

// Breathing room between the bottom of the columns and the bottom of the
// window, and the point below which filling the window stops being useful and
// the columns should just scroll instead. The gutter has to clear the page
// padding that sits below the board (App.js wraps the routes in p-3, and the
// page itself adds 4px) or the last few pixels of it raise a scrollbar.
const BOARD_BOTTOM_GUTTER = 24;
// Matches the column's own min-height, so a very short window scrolls the page
// instead of the two rules fighting over the difference.
const BOARD_MIN_HEIGHT = 420;

/**
 * Height the board can take without pushing the page into a scrollbar.
 *
 * Measured rather than hardcoded as a viewport offset: the filter row above the
 * board wraps at narrow widths and the hero is a different height on mobile, so
 * where the board actually starts is only known at runtime. Re-measured on
 * resize, which is also when the filters rewrap.
 */
const useAvailableBoardHeight = (ref) => {
  const [height, setHeight] = useState(null);

  const measure = useCallback(() => {
    const node = ref.current;
    if (!node) return;
    // Distance from the top of the document: the page does not scroll once the
    // board fits, which is the state this measurement produces.
    const top = node.getBoundingClientRect().top + window.scrollY;
    setHeight(Math.max(BOARD_MIN_HEIGHT, window.innerHeight - top - BOARD_BOTTOM_GUTTER));
  }, [ref]);

  useLayoutEffect(measure, [measure]);

  useEffect(() => {
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [measure]);

  return height;
};

const formatCurrency = (value) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(Number(value) || 0);

// Matches the name the card itself shows, so sorting by name orders the board
// the way it reads.
const employeeName = (expense) => {
  const first = expense?.first_name || expense?.employee_first_name || '';
  const last = expense?.last_name || expense?.employee_last_name || '';
  return `${first} ${last}`.trim() || 'Unknown Employee';
};

const compareByKey = (aName, aAmount, bName, bAmount, key) => (
  key === 'name'
    ? aName.localeCompare(bName, undefined, { sensitivity: 'base' })
    : aAmount - bAmount
);

const blockName = (block) => (block.type === 'group' ? block.group.name || '' : employeeName(block.expense));
const blockAmount = (block) => (
  block.type === 'group' ? Number(block.group.total) || 0 : Number(block.expense?.amount) || 0
);

/**
 * Reorder a column's blocks, and the cards inside each group block with them —
 * a half-sorted group reads as a bug. Groups keep their identity as one unit and
 * sort by their name and combined total, which is what their header shows.
 */
const sortBlocks = (blocks, { key, direction }) => {
  const sign = direction === 'desc' ? -1 : 1;

  return blocks
    .map((block) => (
      block.type === 'group'
        ? {
          ...block,
          group: {
            ...block.group,
            expenses: [...block.group.expenses].sort((a, b) => sign * compareByKey(
              employeeName(a), Number(a?.amount) || 0,
              employeeName(b), Number(b?.amount) || 0,
              key,
            )),
          },
        }
        : block
    ))
    .sort((a, b) => sign * compareByKey(
      blockName(a), blockAmount(a),
      blockName(b), blockAmount(b),
      key,
    ));
};

function DraggableExpenseCard({ expense, stage, inGroup = false, onOpen, onToggleQboReceipt }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: String(expense.id),
    data: { stage },
  });

  return (
    <div
      ref={setNodeRef}
      className={`expense-kanban-card-slot${isDragging ? ' expense-kanban-card-slot--dragging' : ''}`}
      onClick={() => onOpen?.(expense)}
      {...attributes}
      {...listeners}
    >
      <ExpenseKanbanCard expense={expense} inGroup={inGroup} onToggleQboReceipt={onToggleQboReceipt} />
    </div>
  );
}

/**
 * A submitted batch, drawn as one bounded block of cards inside the column.
 *
 * Members stay individually draggable — the group is a visual container, not a
 * unit of work, because its expenses can be approved one at a time and so can
 * sit in different columns. When that happens the header says how many of the
 * batch are in this column, so a partial block doesn't read as the whole batch.
 */
function ExpenseKanbanGroup({ group, memberCount, amountOf, children }) {
  const here = group.expenses.length;
  // Read through the column's own amount so a block total stays a subtotal of
  // the figure in the header above it, caps included or not.
  const total = group.expenses.reduce((sum, expense) => sum + amountOf(expense), 0);
  const split = memberCount > here;

  return (
    <section className="expense-kanban-group" aria-label={`Group ${group.name}`}>
      <header className="expense-kanban-group__head">
        <i className="bi bi-collection" aria-hidden="true" />
        <span className="expense-kanban-group__name" title={group.name}>{group.name}</span>
        <span className="expense-kanban-group__count">
          {split ? `${here} of ${memberCount}` : `${here}`}
        </span>
        <span className="expense-kanban-group__total">{formatCurrency(total)}</span>
      </header>
      <div className="expense-kanban-group__items">{children}</div>
    </section>
  );
}

/**
 * Drag-and-drop presentation of the payroll queues.
 *
 * The board owns the transition rules and the modal that explains a refused
 * move; it owns none of the mutations. A permitted drop is handed back to the
 * page, which runs the same review and mark-paid flows the table view uses.
 */
export default function ExpensePayrollKanban({
  expenses,
  currentEmployeeId,
  onOpenExpense,
  onApprove,
  onMarkPaid,
  onMarkInvoiced,
  onEditInvoiceCap,
  onRevertStage,
  onToggleQboReceipt,
}) {
  const [activeId, setActiveId] = useState(null);
  const [draggedExpense, setDraggedExpense] = useState(null);
  const [dropStates, setDropStates] = useState({});
  const [blockedMove, setBlockedMove] = useState(null);
  const blockedMoveShown = useLastPresent(blockedMove); // keeps the dialog steady while it fades out
  // Per column, because the buckets are read one at a time — you sort Awaiting
  // Reimbursement by amount to find the big ones without disturbing the others.
  const [sortByStage, setSortByStage] = useState({});
  // Off by default: the Invoiced total answers "what did we lay out" first, and
  // "what were we allowed to bill" is the deliberate second look.
  const [includeCaps, setIncludeCaps] = useState(false);
  const boardRef = useRef(null);
  const availableHeight = useAvailableBoardHeight(boardRef);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: DRAG_ACTIVATION_DISTANCE } }),
    useSensor(KeyboardSensor),
  );

  const columns = useMemo(() => {
    const byStage = new Map(EXPENSE_STAGES.map((stage) => [stage.id, []]));
    (expenses || []).forEach((expense) => {
      const stage = getExpenseStage(expense);
      if (stage) byStage.get(stage).push(expense);
    });

    return EXPENSE_STAGES.map((stage) => {
      const items = byStage.get(stage.id);
      // Grouped members are drawn together inside the column; loose expenses
      // keep their place in the same newest-first order until a sort is chosen.
      const blocks = buildExpenseGroupBlocks(items);
      const sort = sortByStage[stage.id];
      // Caps only exist on invoiced expenses, so the toggle only reaches that
      // column. Totals read through this; the cards keep showing what we paid,
      // and so does the amount sort, which would otherwise rank the column in
      // an order the cards contradict.
      const amountOf = stage.id === STAGE_INVOICED && includeCaps
        ? getInvoicedAmount
        : (expense) => Number(expense?.amount) || 0;

      return {
        ...stage,
        items,
        blocks: sort ? sortBlocks(blocks, sort) : blocks,
        sort: sort || null,
        amountOf,
        total: items.reduce((sum, expense) => sum + amountOf(expense), 0),
      };
    });
  }, [expenses, sortByStage, includeCaps]);

  // Ascending, then descending, then back to the board's own newest-first order.
  // Picking the other key starts it over at ascending rather than inheriting a
  // direction the button was never showing.
  const cycleSort = useCallback((stageId, key) => {
    setSortByStage((prev) => {
      const current = prev[stageId];
      let next;
      if (!current || current.key !== key) next = { key, direction: 'asc' };
      else if (current.direction === 'asc') next = { key, direction: 'desc' };
      else next = null;
      return { ...prev, [stageId]: next };
    });
  }, []);

  // Counted across the whole board, not per column: a group whose expenses have
  // been approved one at a time is split between columns, and each block needs
  // to know it is only showing part of the batch.
  const groupSizes = useMemo(() => {
    const sizes = new Map();
    (expenses || []).forEach((expense) => {
      const groupId = expense?.group_id;
      if (!groupId) return;
      const key = String(groupId);
      sizes.set(key, (sizes.get(key) || 0) + 1);
    });
    return sizes;
  }, [expenses]);

  const handleDragStart = useCallback((event) => {
    const expense = (expenses || []).find((item) => String(item.id) === String(event.active.id));
    setActiveId(event.active.id);
    setDraggedExpense(expense || null);

    if (!expense) {
      setDropStates({});
      return;
    }

    // Score every column up front so the whole board reads as a legend the
    // moment the drag begins, rather than only under the pointer.
    const fromStage = getExpenseStage(expense);
    const next = {};
    EXPENSE_STAGES.forEach((stage) => {
      if (stage.id === fromStage) return;
      const verdict = evaluateExpenseTransition({ expense, toStage: stage.id, currentEmployeeId });
      // A column the drag can reach by overruling the rule is painted apart
      // from one it cannot reach at all: amber reads as "ask first", not "no".
      if (verdict.allowed) next[stage.id] = 'valid';
      else next[stage.id] = verdict.override ? 'override' : 'blocked';
    });
    setDropStates(next);
  }, [expenses, currentEmployeeId]);

  const clearDrag = useCallback(() => {
    setActiveId(null);
    setDraggedExpense(null);
    setDropStates({});
  }, []);

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    clearDrag();
    if (!over) return;

    const expense = (expenses || []).find((item) => String(item.id) === String(active.id));
    if (!expense) return;

    const toStage = String(over.id);
    const verdict = evaluateExpenseTransition({ expense, toStage, currentEmployeeId });

    if (!verdict.allowed) {
      // A drop back on the card's own column carries no explanation; it is just
      // a cancelled drag.
      if (verdict.reason) setBlockedMove({ expense, toStage, verdict });
      return;
    }

    if (verdict.action === 'approve') onApprove?.(expense);
    else if (verdict.action === 'markPaid') onMarkPaid?.(expense);
    else if (verdict.action === 'markInvoiced') onMarkInvoiced?.(expense);
  }, [expenses, currentEmployeeId, clearDrag, onApprove, onMarkPaid, onMarkInvoiced]);

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={pointerWithin}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={clearDrag}
        autoScroll={{ enabled: true }}
      >
        <div
          ref={boardRef}
          className={`expense-kanban${activeId ? ' expense-kanban--dragging' : ''}`}
          // Consumed by the column's max-height. Only the wide, single-row
          // layout uses it; the wrapped layouts fall back to their own caps.
          style={availableHeight ? { '--kanban-available': `${availableHeight}px` } : undefined}
        >
          {columns.map((column) => (
            <ExpenseKanbanColumn
              key={column.id}
              id={column.id}
              title={column.title}
              count={column.items.length}
              total={column.total}
              dropState={dropStates[column.id] || null}
              sort={column.sort}
              onCycleSort={(key) => cycleSort(column.id, key)}
              includeCaps={includeCaps}
              onToggleIncludeCaps={column.id === STAGE_INVOICED ? setIncludeCaps : undefined}
            >
              {column.blocks.map((block) => {
                // Only settled expenses carry a QBO receipt worth tracking,
                // which matches the table view's showQboReceipt column. An
                // invoiced expense is settled too, and its receipt is just as
                // likely to still be waiting to go up.
                const qboHandler = column.id === STAGE_PAID || column.id === STAGE_INVOICED
                  ? onToggleQboReceipt
                  : undefined;

                // Nothing in the Invoiced bucket is waiting on a decision, so a
                // click there goes to the one figure that is still worth
                // changing rather than to the review modal. That dialog offers
                // the way through to the full expense.
                const openHandler = column.id === STAGE_INVOICED && onEditInvoiceCap
                  ? onEditInvoiceCap
                  : onOpenExpense;

                if (block.type === 'group') {
                  return (
                    <ExpenseKanbanGroup
                      key={block.key}
                      group={block.group}
                      memberCount={groupSizes.get(String(block.group.id)) || block.group.expenses.length}
                      amountOf={column.amountOf}
                    >
                      {block.group.expenses.map((expense) => (
                        <DraggableExpenseCard
                          key={expense.id}
                          expense={expense}
                          stage={column.id}
                          inGroup
                          onOpen={openHandler}
                          onToggleQboReceipt={qboHandler}
                        />
                      ))}
                    </ExpenseKanbanGroup>
                  );
                }

                return (
                  <DraggableExpenseCard
                    key={block.key}
                    expense={block.expense}
                    stage={column.id}
                    onOpen={openHandler}
                    onToggleQboReceipt={qboHandler}
                  />
                );
              })}
            </ExpenseKanbanColumn>
          ))}
        </div>

        {createPortal(
          <DragOverlay dropAnimation={null}>
            {activeId && draggedExpense ? (
              <ExpenseKanbanCard expense={draggedExpense} isOverlay />
            ) : null}
          </DragOverlay>,
          document.body,
        )}
      </DndContext>

      <Modal open={Boolean(blockedMove)} onClose={() => setBlockedMove(null)} size="xs">
        <Modal.Header>
          <Modal.Title>{blockedMoveShown?.verdict.title || 'Move not allowed'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="expense-kanban-dialog__lead">
            {getStageTitle(getExpenseStage(blockedMoveShown?.expense))}
            {' → '}
            {getStageTitle(blockedMoveShown?.toStage)}
          </p>
          <p className="expense-kanban-dialog__body">{blockedMoveShown?.verdict.reason}</p>
        </Modal.Body>
        {/* The override is deliberately not the primary button: the dialog
            opened to say no, and saying yes anyway is the second thought. */}
        <Modal.Footer
          left={blockedMoveShown?.verdict.override ? (
            <Button
              variant="danger"
              disabled={!blockedMove}
              onClick={() => {
                const { expense, verdict } = blockedMove;
                setBlockedMove(null);
                onRevertStage?.(expense, verdict.override.toStage);
              }}
            >
              {blockedMoveShown.verdict.override.label}
            </Button>
          ) : null}
        >
          <Button variant="primary" onClick={() => setBlockedMove(null)}>
            {blockedMoveShown?.verdict.override ? 'Cancel' : 'Got it'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
